import { Info, Radio, Clock, FlaskConical, Ban, Globe } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { usePrefs } from "@/context/PrefsContext";

const DATA = {
  realtime: { label: "AO VIVO", cls: "text-up border-up/40 bg-up-soft", icon: Radio, tip: "Cotação em tempo real do provedor público Binance." },
  market: { label: "REAL", cls: "text-sky-400 border-sky-400/40 bg-sky-400/10", icon: Globe, tip: "Cotação real do Google Finance, atualizada a cada 30 s. Pode haver atraso de até 15–20 min conforme a bolsa. Histórico do gráfico: Yahoo Finance." },
  delayed: { label: "ATRASADO", cls: "text-amber-500 border-amber-500/40 bg-amber-500/10", icon: Clock, tip: "O provedor não respondeu recentemente; último valor conhecido." },
  indicative: { label: "INDICATIVO", cls: "text-sky-500 border-sky-500/40 bg-sky-500/10", icon: Info, tip: "Valor de referência, não executável." },
  simulated: { label: "SIMULADO", cls: "text-violet-400 border-violet-400/40 bg-violet-400/10", icon: FlaskConical, tip: "Cotação gerada pelo motor de simulação. Não é preço de mercado real." },
  unavailable: { label: "INDISPONÍVEL", cls: "text-muted-foreground border-border", icon: Ban, tip: "Sem dados no momento." },
};

export const DataBadge = ({ type = "unavailable", testid }) => {
  const d = DATA[type] || DATA.unavailable;
  const Icon = d.icon;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span data-testid={testid || `data-badge-${type}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold tracking-wider ${d.cls}`}>
          <Icon className={`h-3 w-3 ${type === "realtime" ? "live-dot" : ""}`} />{d.label}
        </span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs">{d.tip}</TooltipContent>
    </Tooltip>
  );
};

const TONES = {
  up: "text-up bg-up-soft border-up/30", down: "text-down bg-down-soft border-down/30", warn: "text-amber-500 bg-amber-500/10 border-amber-500/30",
  info: "text-sky-500 bg-sky-500/10 border-sky-500/30", muted: "text-muted-foreground bg-muted border-border",
};

export const StatusBadge = ({ map, value, testid }) => {
  const s = map[value] || { label: value, tone: "muted" };
  return <span data-testid={testid} className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${TONES[s.tone]}`}>{s.label}</span>;
};

export const MarketStatus = ({ status }) => {
  const m = { open: ["Aberto", "bg-up live-dot"], closed: ["Fechado", "bg-muted-foreground"], unavailable: ["Indisponível", "bg-down"] }[status] || ["—", "bg-muted"];
  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" data-testid="market-status">
      <span className={`h-1.5 w-1.5 rounded-full ${m[1]}`} />{m[0]}
    </span>
  );
};

const CAT_COLORS = { forex: "#1E3A8A", crypto: "#F59E0B", stocks: "#0F766E", indices: "#7C3AED", commodities: "#B45309" };

export const AssetIcon = ({ symbol, category, size = 36 }) => {
  const label = category === "forex" ? symbol.slice(0, 3) : symbol.replace("USDT", "").replace("USD", "").slice(0, 4);
  return (
    <div className="relative shrink-0 grid place-items-center rounded-xl font-mono font-bold text-white"
      style={{ width: size, height: size, background: CAT_COLORS[category] || "#334155", fontSize: size * (label.length > 3 ? 0.26 : 0.3) }}>
      {label}
      {category === "forex" && (
        <span className="absolute -bottom-1 -right-1 rounded-md bg-card border border-border px-1 text-[9px] text-foreground">{symbol.slice(3, 6)}</span>
      )}
    </div>
  );
};

export const StatCard = ({ label, value, sub, tone, testid, help, className = "" }) => (
  <div className={`surface p-5 fade-up ${className}`} data-testid={testid}>
    <div className="flex items-center gap-1.5 eyebrow">{label}{help && <HelpTip text={help} />}</div>
    <div className={`num mt-3 text-2xl font-semibold ${tone || ""}`} data-testid={testid ? `${testid}-value` : undefined}>{value}</div>
    {sub && <div className="mt-1.5 text-xs text-muted-foreground">{sub}</div>}
  </div>
);

export const HelpTip = ({ text }) => {
  const { beginner } = usePrefs();
  if (!beginner) return null;
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button type="button" aria-label="Ajuda" className="text-muted-foreground hover:text-primary normal-case tracking-normal"><Info className="h-3.5 w-3.5" /></button>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-sm">{text}</TooltipContent>
    </Tooltip>
  );
};

export const PageHeader = ({ eyebrow, title, desc, actions }) => (
  <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between fade-up">
    <div>
      {eyebrow && <div className="eyebrow mb-2">{eyebrow}</div>}
      <h1 className="text-3xl md:text-4xl font-bold tracking-tight" data-testid="page-title">{title}</h1>
      {desc && <p className="mt-2 max-w-2xl text-sm md:text-base text-muted-foreground">{desc}</p>}
    </div>
    {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
  </div>
);

export const Empty = ({ text, testid }) => (
  <div className="py-10 text-center text-sm text-muted-foreground" data-testid={testid || "empty-state"}>{text}</div>
);

export const DemoBanner = ({ text }) => (
  <div className="flex items-start gap-3 rounded-xl border border-violet-400/30 bg-violet-400/10 px-4 py-3 text-sm" data-testid="demo-banner">
    <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-violet-400" />
    <span>{text}</span>
  </div>
);
